import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';

interface CompanyDetails {
  name: string;
  status: string;
  country: string;
  city: string;
  postal_code: string;
  vat_number: string;
  eori_number: string;
  coc_number: number;
  article_23: boolean;
  phone_number: string;
  created_by_user_id?: string;
  street: string | null;
  house_number: string | null;
  // Picqer integration fields
  picqer_fulfilment_customer_id?: number;
  picqer_enabled?: boolean;
  picqer_api_key?: string; // Only included when specifically needed for operations
  picqer_manual_orders_enabled?: boolean;
  // WMS provider and ChannelDock
  wms_provider?: 'picqer' | 'channeldock' | null;
  channeldock_enabled?: boolean;
  channeldock_api_key?: string;
  channeldock_api_secret?: string;
}

export const useCompanyDetails = (companyId: number | null) => {
  const [details, setDetails] = useState<CompanyDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchDetails = async () => {
      setLoading(true);
      if (!companyId) {
        setDetails(null);
        setLoading(false);
        return;
      }
      
      try {
        const { data, error } = await supabase
          .from('company')
          .select('*')
          .eq('id', companyId)
          .single();

        if (error) {
          // Handle specific error cases
          if (error.code === "PGRST116" || error.message?.includes("JSON object requested, multiple (or no) rows returned")) {
            console.warn(`Company with ID ${companyId} not found. This could be due to the company being deleted or the user not having access.`);
            // Clear the selected company from localStorage
            localStorage.removeItem("selectedCompany");
            throw new Error(`Company with ID ${companyId} not found`);
          }
          throw error;
        }
        setDetails(data);
      } catch (err) {
        console.error("Error fetching company details:", err);
        setError(err instanceof Error ? err.message : 'Failed to fetch company details');
        setDetails(null);
      } finally {
        setLoading(false);
      }
    };

    fetchDetails();
  }, [companyId]); // Re-fetch when companyId changes

  const updateCompany = async (values: CompanyDetails) => {
    if (!companyId) return false;
    
    try {
      const { error } = await supabase
        .from('company')
        .update(values)
        .eq('id', companyId);

      if (error) throw error;
      setDetails(values);
      return true;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update company');
      return false;
    }
  };

  return { details, loading, error, updateCompany };
}; 